import { apiGet, apiPatch } from './client';
import type {
  TaseIdxComposition,
  TaseIdxCompositionBatchRequest,
  TaseIdxCompositionKey,
} from '../types/TaseIdxComposition';

function withIdKey(
  rows: Omit<TaseIdxComposition, 'idKey'>[],
): TaseIdxComposition[] {
  return rows.map((r) => ({
    ...r,
    idKey: `${r.dDate}|${r.taseIdxId}|${r.assetId}`,
  }));
}

function stripIdKey(
  rows: TaseIdxComposition[],
): Omit<TaseIdxComposition, 'idKey'>[] {
  return rows.map(({ idKey, ...rest }) => rest);
}

function parseIdKey(idKey: string): TaseIdxCompositionKey {
  const [d, ti, a] = idKey.split('|');
  return { dDate: d, taseIdxId: Number(ti), assetId: Number(a) };
}

function buildQuery(date?: string, taseIdxId?: number): string {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (taseIdxId != null) params.set('taseIdxId', String(taseIdxId));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getTaseIdxCompositions(
  date?: string,
  taseIdxId?: number,
): Promise<TaseIdxComposition[]> {
  const rows = await apiGet<Omit<TaseIdxComposition, 'idKey'>[]>(
    `/api/tase-idx-compositions${buildQuery(date, taseIdxId)}`,
  );
  return withIdKey(rows);
}

export async function saveTaseIdxCompositionsBatch(
  added: TaseIdxComposition[],
  modified: TaseIdxComposition[],
  deletedIdKeys: string[],
  date?: string,
  taseIdxId?: number,
): Promise<TaseIdxComposition[]> {
  const body: TaseIdxCompositionBatchRequest = {
    added: stripIdKey(added),
    modified: stripIdKey(modified),
    deleted: deletedIdKeys.map(parseIdKey),
    date,
    taseIdxId,
  };
  const fresh = await apiPatch<
    TaseIdxCompositionBatchRequest,
    Omit<TaseIdxComposition, 'idKey'>[]
  >('/api/tase-idx-compositions/batch', body);
  return withIdKey(fresh);
}
